import { createContext, useContext, useEffect, useState, useCallback, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { ToastAction } from '../components/ui/Toast/Toast';
import { getJsonAuthHeaders } from '../utils/getAccessToken';

// Types
interface PendingGuide {
  guideId: string;
  startedAt: number;
  status: string;
  title?: string;
}

interface GuideCreationContextValue {
  /** Add a guide to background polling */
  startPolling: (guideId: string, title?: string) => void;
  /** Currently polling guides */
  pendingGuides: PendingGuide[];
  /** Check if a specific guide is being polled */
  isPolling: (guideId: string) => boolean;
  /** Total count of guides being created */
  pendingCount: number;
}

const GuideCreationContext = createContext<GuideCreationContextValue | null>(null);

const STORAGE_KEY = 'videreo_pending_guides';
const INITIAL_POLLING_INTERVAL = 10000; // 10 seconds for first minute
const FAST_POLLING_INTERVAL = 5000; // 5 seconds after first minute
const FAST_POLLING_THRESHOLD = 60000; // Switch to fast polling after 60 seconds
const MAX_POLLING_TIME = 10 * 60 * 1000; // 10 minutes

// Load pending guides from localStorage
function loadPendingGuides(): PendingGuide[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const guides = JSON.parse(stored) as PendingGuide[];
    // Filter out guides that have been polling too long (stale)
    const now = Date.now();
    return guides.filter(g => now - g.startedAt < MAX_POLLING_TIME);
  } catch {
    return [];
  }
}

// Save pending guides to localStorage
function savePendingGuides(guides: PendingGuide[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(guides));
  } catch {
    // Ignore storage errors
  }
}

export function GuideCreationProvider({ children }: { children: ReactNode }) {
  const [pendingGuides, setPendingGuides] = useState<PendingGuide[]>(loadPendingGuides);
  const { toast } = useToast();
  const navigate = useNavigate();
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingGuidesRef = useRef<PendingGuide[]>(pendingGuides);

  // Keep ref in sync with state
  useEffect(() => {
    pendingGuidesRef.current = pendingGuides;
  }, [pendingGuides]);

  // Calculate polling interval based on oldest pending guide
  const getPollingInterval = useCallback((guides: PendingGuide[]) => {
    if (guides.length === 0) return INITIAL_POLLING_INTERVAL;
    const now = Date.now();
    const oldestGuide = guides.reduce((oldest, g) => 
      g.startedAt < oldest.startedAt ? g : oldest
    );
    const elapsed = now - oldestGuide.startedAt;
    return elapsed >= FAST_POLLING_THRESHOLD ? FAST_POLLING_INTERVAL : INITIAL_POLLING_INTERVAL;
  }, []);

  // Persist to localStorage whenever pendingGuides changes
  useEffect(() => {
    savePendingGuides(pendingGuides);
  }, [pendingGuides]);

  // Poll for guide status
  const pollGuideStatus = useCallback(async (guideId: string) => {
    try {
      const headers = await getJsonAuthHeaders();
      const res = await fetch('/api/supabase/portal-guide-polling', {
        method: 'POST',
        headers,
        body: JSON.stringify({ p_guide_id: guideId }),
      });
      
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }, []);

  // Main polling effect
  useEffect(() => {
    if (pendingGuides.length === 0) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      return;
    }

    let isMounted = true;

    const checkAllGuides = async () => {
      const currentGuides = pendingGuidesRef.current;
      if (currentGuides.length === 0) return;

      const now = Date.now();
      const updates: PendingGuide[] = [];
      const completed: PendingGuide[] = [];
      const timedOut: PendingGuide[] = [];

      for (const guide of currentGuides) {
        // Check for timeout
        if (now - guide.startedAt > MAX_POLLING_TIME) {
          timedOut.push(guide);
          continue;
        }

        const status = await pollGuideStatus(guide.guideId);
        
        if (status?.stop || status?.status === 'completed') {
          completed.push({ ...guide, status: status.status });
        } else if (status?.status === 'errored') {
          // Handle error - remove from polling and notify
          toast({
            title: 'Guide Creation Failed',
            description: status.error_message || 'There was an error creating your guide.',
            variant: 'destructive',
          });
        } else {
          // Still in progress
          updates.push({ 
            ...guide, 
            status: status?.status || guide.status 
          });
        }
      }

      // Notify about completed guides
      for (const guide of completed) {
        // Small delay to ensure any previous toast is cleared first
        setTimeout(() => {
          toast({
            title: '🎉 Your guide is ready!',
            description: guide.title ? `"${guide.title}" has been created successfully.` : 'Your guide has been created successfully.',
            action: (
              <ToastAction 
                altText="Edit Guide"
                onClick={() => navigate(`/guides/edit/${guide.guideId}`)}
              >
                Edit Guide
              </ToastAction>
            ),
            duration: 15000, // Show for 15 seconds
          });
        }, 300);
        
        // Dispatch event to refresh guides list
        try {
          window.dispatchEvent(new CustomEvent('guide:created', { detail: { guideId: guide.guideId } }));
        } catch {}
      }

      // Notify about timed out guides
      for (const _guide of timedOut) {
        toast({
          title: 'Guide Creation Timeout',
          description: 'The guide is taking longer than expected. Check back later.',
          variant: 'destructive',
        });
      }

      // Update state - only keep guides still in progress
      setPendingGuides(updates);

      // Schedule next poll with dynamic interval
      if (isMounted && updates.length > 0) {
        const interval = getPollingInterval(updates);
        pollTimeoutRef.current = setTimeout(checkAllGuides, interval);
      }
    };

    // Initial check after a short delay
    const interval = getPollingInterval(pendingGuides);
    pollTimeoutRef.current = setTimeout(checkAllGuides, interval);

    return () => {
      isMounted = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGuides.length, pollGuideStatus, toast, navigate, getPollingInterval]);

  const startPolling = useCallback((guideId: string, title?: string) => {
    setPendingGuides(prev => {
      // Don't add if already polling
      if (prev.some(g => g.guideId === guideId)) return prev;
      
      return [...prev, {
        guideId,
        startedAt: Date.now(),
        status: 'getting_started',
        title,
      }];
    });

    // Show initial toast
    toast({
      title: '✨ Creating Your Guide',
      description: 'Our AI is crafting your guide. This takes 1-2 minutes — feel free to keep exploring!',
      duration: 5000,
    });
  }, [toast]);

  const isPolling = useCallback((guideId: string) => {
    return pendingGuides.some(g => g.guideId === guideId);
  }, [pendingGuides]);

  const value: GuideCreationContextValue = {
    startPolling,
    pendingGuides,
    isPolling,
    pendingCount: pendingGuides.length,
  };

  return (
    <GuideCreationContext.Provider value={value}>
      {children}
    </GuideCreationContext.Provider>
  );
}

export function useGuideCreation() {
  const context = useContext(GuideCreationContext);
  if (!context) {
    throw new Error('useGuideCreation must be used within a GuideCreationProvider');
  }
  return context;
}
